import { NavLink } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import type { NavItem } from "../navigation/navigation";

export interface SidebarNavItemProps {
  item: NavItem;
  collapsed?: boolean;
  /** La sección enlaza pantallas de fuera del armazón. */
  external?: boolean;
  /** Cerrar el cajón al navegar, en móvil. */
  onNavigate?: () => void;
}

/**
 * Un enlace de la barra lateral.
 *
 * ── El estado activo ─────────────────────────────────────────────────
 * `NavLink` pone `aria-current="page"` él solo cuando la ruta coincide,
 * así que el estado no se anuncia únicamente por color. Lo visual va
 * encima: la tesela sube a vidrio blanco con borde y aparece una barra
 * violeta a la izquierda — dos señales además del color, que es lo que
 * pide no depender del color a secas.
 *
 * ── Plegada ──────────────────────────────────────────────────────────
 * Un icono solo no comunica, así que en el riel el enlace lleva
 * `aria-label` con su nombre (el mismo texto que se lee desplegado) y una
 * etiqueta flotante que aparece al pasar el ratón o al recibir el foco.
 * La etiqueta es `aria-hidden`: el nombre ya lo da el `aria-label`, y
 * leerlo dos veces es ruido.
 */
export function SidebarNavItem({
  item,
  collapsed = false,
  external = false,
  onNavigate,
}: SidebarNavItemProps) {
  const { t } = useTranslation();
  const Icon = item.icon;
  const label = t(item.label);

  return (
    <li className="group relative">
      <NavLink
        to={item.to}
        /*
         * `end` cuando el destino es prefijo de OTROS destinos del mapa
         * — las TRES puertas de panel: sin esto, el Dashboard/Resumen
         * quedaba iluminado en cualquier pantalla de su panel.
         */
        end={["/admin", "/socio", "/mi-negocio"].includes(item.to)}
        onClick={onNavigate}
        aria-label={collapsed ? label : undefined}
        className={({ isActive }) =>
          cn(
            "relative flex items-center gap-2.5 rounded-2xl text-[13px] font-semibold outline-none",
            "transition-colors duration-200 motion-reduce:transition-none",
            "focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent",
            collapsed ? "h-11 w-11 justify-center px-0" : "px-3 py-2.5",
            isActive
              ? "border border-ds-border bg-white/75 text-ds-violet-pop shadow-ds-xs"
              : "border border-transparent text-ds-ink-3 hover:bg-white/45 hover:text-ds-ink",
          )
        }
      >
        {({ isActive }) => (
          <>
            {/* La barra del activo. Decorativa: el estado ya viaja en aria-current. */}
            {isActive && (
              <span
                aria-hidden="true"
                className={cn(
                  "absolute rounded-full bg-ds-violet-pop",
                  collapsed
                    ? "inset-x-3 -bottom-1 h-[3px]"
                    : "inset-y-2 left-0 w-[3px] -translate-x-px",
                )}
              />
            )}
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
            {!collapsed && external && (
              <ArrowUpRight aria-hidden="true" className="h-3.5 w-3.5 shrink-0 opacity-70" />
            )}
          </>
        )}
      </NavLink>

      {collapsed && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap",
            "rounded-xl border border-ds-border bg-white px-2.5 py-1.5 text-[12px] font-semibold text-ds-ink shadow-ds-card",
            "opacity-0 transition-opacity duration-150 motion-reduce:transition-none",
            "group-hover:opacity-100 group-focus-within:opacity-100",
          )}
        >
          {label}
        </span>
      )}
    </li>
  );
}
